import { buildAPIUrl } from "../../api/apiHelpers";
import { API_BASE_URL } from "../../constants/index";
import HttpInterface from "../../helpers/HttpInterface";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam";
import {
  CreateWarehouseArticleSettings,
  UpdateWarehouseArticleSettings,
} from "../../Models/WarehouseArticleSettings";

export class WarehouseArticleSettingsService {
  private httpProxy: HttpInterface; // Instance of the HttpInterface to make HTTP requests

  // Constructor initializes the httpProxy with the HttpInterface
  constructor() {
    this.httpProxy = new HttpInterface(); // Creates an instance of the HttpInterface to handle requests
  }

  /**
   * Fetches all warehouse article settings from the logistic service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing the list of warehouses.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Builds the complete API URL using the base URL, endpoint, and parameters
    return this.httpProxy.fetch(
      buildAPIUrl(
        `${API_BASE_URL}/logistic-service/warehouse-article-settings`,
        params,
      ),
      requestOptions,
    );
  }

  /**
   * Fetches a specific warehouse by its ID from the API.
   *
   * @param id The unique identifier of the warehouse to fetch.
   * @returns A promise that resolves with the API response containing the warehouse details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific warehouse by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/warehouse-article-settings/${id}`,
      requestOptions,
    );
  }

  /**
   * Creates a new warehouse in the logistic service API.
   *
   * @param warehouse The details of the warehouse to create.
   * @returns A promise that resolves with the API response confirming the creation of the warehouse.
   */
  create(warehouse: CreateWarehouseArticleSettings): Promise<any> {
    const requestOptions = {
      method: "POST", // HTTP method for the request
      body: JSON.stringify(warehouse), // Sends the warehouse data as JSON in the request body
    };

    // Sends a POST request to create the new warehouse
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/warehouse-article-settings`,
      requestOptions,
    );
  }

  /**
   * Updates an existing warehouse in the logistic service API.
   *
   * @param warehouse The updated details of the warehouse.
   * @returns A promise that resolves with the API response confirming the update.
   */
  edit(warehouse: UpdateWarehouseArticleSettings): Promise<any> {
    const requestOptions = {
      method: "PUT", // HTTP method for the request
      body: JSON.stringify(warehouse), // Sends the updated warehouse data as JSON in the request body
    };

    // Sends a PUT request to update the specific warehouse by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/warehouse-article-settings/${warehouse.id}`,
      requestOptions,
    );
  }

  /**
   * Deletes a warehouse from the logistic service API.
   *
   * @param id The unique identifier of the warehouse to delete.
   * @returns A promise that resolves with the API response confirming the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE", // HTTP method for the request
    };

    // Sends a DELETE request to remove the specific warehouse by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/logistic-service/warehouse-article-settings/${id}`,
      requestOptions,
    );
  }
}
